export const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userID",
  "userName",
  "userEmail",
  "userRole",
  "userFacultyId",
  "userLastLoginAt",
  "userPreviousLastLoginAt",
  "tutorViewMode",
];

export const AUTH_EVENT_KEY = "__lh_auth_event__";
export const AUTH_CHANGE_EVENT = "auth:changed";
const LOGIN_HISTORY_PREFIX = "learnhut:last-login-at:";

const readRawToken = () =>
  localStorage.getItem("authToken") ||
  localStorage.getItem("accessToken") ||
  localStorage.getItem("token") ||
  "";

const base64UrlDecode = (value) => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (normalized.length % 4)) % 4;
  const padded = `${normalized}${"=".repeat(padLen)}`;
  try {
    return atob(padded);
  } catch {
    return "";
  }
};

const parseJwtPayload = (token) => {
  const parts = String(token || "").split(".");
  if (parts.length < 2) return null;

  const decoded = base64UrlDecode(parts[1]);
  if (!decoded) return null;

  try {
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const parseExpiryMs = (value, assumeSeconds = false) => {
  if (value === undefined || value === null || value === "") return null;

  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return assumeSeconds || numeric < 1000000000000 ? numeric * 1000 : numeric;
  }

  if (typeof value !== "string") return null;

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const getTokenExpiryMs = (token) => {
  const payload = parseJwtPayload(token);

  // Standard JWT expiry is seconds since epoch.
  const expMs = parseExpiryMs(payload?.exp, true);
  if (expMs) return expMs;

  // Go backend tokens store time.Time as an RFC3339 string in expired_at.
  return (
    parseExpiryMs(payload?.expired_at) ||
    parseExpiryMs(payload?.ExpiredAt) ||
    parseExpiryMs(payload?.expires_at)
  );
};

export const getAuthExpiryMs = () => getTokenExpiryMs(readRawToken());

export const isTokenExpired = (token, skewSeconds = 15) => {
  if (!token) return true;
  const expMs = getTokenExpiryMs(token);

  // If token has no recognized expiry claim, rely on backend auth checks.
  if (!expMs) return false;

  return Date.now() >= expMs - skewSeconds * 1000;
};

export const getAuthToken = () => {
  const token = readRawToken();
  if (!token) return "";
  if (isTokenExpired(token)) return "";
  return token;
};

const hasStoredAuthData = () =>
  AUTH_STORAGE_KEYS.some((key) => Boolean(localStorage.getItem(key)));

export const clearExpiredAuthSession = () => {
  const token = readRawToken();

  if (!token) {
    if (hasStoredAuthData()) {
      clearAuthSession();
      return true;
    }
    return false;
  }

  if (isTokenExpired(token, 0)) {
    clearAuthSession();
    return true;
  }

  return false;
};

export const isAuthenticated = () => {
  const token = getAuthToken();
  const userId = localStorage.getItem("userID") || "";
  return Boolean(token && userId);
};

export const getDefaultAuthedRoute = () => {
  const role = localStorage.getItem("userRole");
  const isTutor = role === "TUTOR" || role === "ADMIN";
  if (!isTutor) return "/dashboard";

  const mode = localStorage.getItem("tutorViewMode");
  return mode === "student" ? "/dashboard" : "/dashboard-tutor";
};

const emitAuthChanged = (type) => {
  localStorage.setItem(
    AUTH_EVENT_KEY,
    JSON.stringify({
      type,
      ts: Date.now(),
    })
  );
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

export const persistAuthSession = ({ token, user }) => {
  const userId = user?.id || "";
  const storedPreviousLoginAt = userId
    ? localStorage.getItem(`${LOGIN_HISTORY_PREFIX}${userId}`) || ""
    : "";
  const currentLoginAt = new Date().toISOString();

  localStorage.setItem("authToken", token || "");
  localStorage.setItem("userID", userId);
  localStorage.setItem("userName", user?.name || "");
  localStorage.setItem("userEmail", user?.email || "");
  localStorage.setItem("userRole", user?.role || "STUDENT");
  localStorage.setItem("userFacultyId", user?.facultyId || user?.faculty_id || "");
  localStorage.setItem(
    "userLastLoginAt",
    user?.lastLoginAt || user?.last_login_at || ""
  );
  localStorage.setItem(
    "userPreviousLastLoginAt",
    user?.previousLastLoginAt ||
      user?.previous_last_login_at ||
      user?.lastLoginAtBeforeUpdate ||
      user?.last_login_at_before_update ||
      storedPreviousLoginAt
  );

  if (userId) {
    localStorage.setItem(`${LOGIN_HISTORY_PREFIX}${userId}`, currentLoginAt);
  }

  const isTutor = user?.role === "TUTOR" || user?.role === "ADMIN";
  localStorage.setItem("tutorViewMode", isTutor ? "tutor" : "student");

  emitAuthChanged("login");
};

export const clearAuthSession = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  emitAuthChanged("logout");
};
